// Tela inicial com:
// - Logo e título
// - Botão de login com Google (Firebase)
// - Ilustração/background temático
// - Responsividade completa
// - Tratamento de erros

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogIn, AlertCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import TermsOfServiceModal from './TermsOfServiceModal';

// Logo da aplicação
import logoSvg from '@/assets/images/nexusprime.svg';

export default function LoginScreen() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const { login } = useAuth();
  const { success, error: showError } = useToast();

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      await login();
      success(t('auth.loginSuccess'));
    } catch (err) {
      console.error('Erro ao fazer login:', err);
      showError(err.message || t('auth.loginError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <img src={logoSvg} alt="Logo" className="h-20 w-auto" />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Nexusprime</h1>
          <p className="text-purple-300">{t('auth.tagline')}</p>
        </div>

        <button 
          onClick={handleGoogleLogin}
          disabled={isLoading}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-semibold py-3 px-4 rounded-2xl flex items-center justify-center gap-2 transition disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <div className="animate-spin">
                <LogIn size={20} />
              </div>
              {t('auth.connecting')}
            </>
          ) : (
            <>
              <LogIn size={20} />
              {t('auth.loginWithGoogle')}
            </>
          )}
        </button>

        <div className="mt-8 p-4 bg-blue-900/20 border border-blue-700/50 rounded-xl flex gap-3">
          <AlertCircle size={20} className="text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-300">
            {t('auth.termsHint')}{' '}
            <button
              type="button"
              onClick={() => setShowTerms(true)}
              className="text-blue-200 cursor-pointer hover:underline focus:outline-none focus:underline"
            >
              {t('auth.termsLink')}
            </button>
          </p>
        </div>
      </div>

      <TermsOfServiceModal isOpen={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
}
